import { useFooterType } from '@hooks/useFooterType';
import { useNavbarColor } from '@hooks/useNavbarColor';
import { useNavbarType } from '@hooks/useNavbarType';
import { useSkin } from '@hooks/useSkin';
import { useLayoutContext } from '@src/@data/store/LayoutProvider';
import { CookieBanner } from '@src/components/organisms/cookie-banner/CookieBanner';
import '@styles/base/core/menu/menu-types/vertical-menu.scss';
import '@styles/base/core/menu/menu-types/vertical-overlay-menu.scss';
import classnames from 'classnames';
import { useEffect, useState } from 'react';
import { ArrowUp } from 'react-feather';
import { useLocation } from 'react-router-dom';
import ScrollToTop from 'react-scroll-up';
import { Button, Navbar } from 'reactstrap';
import FooterComponent from './components/footer';
import SidebarComponent from './components/menu/vertical-menu';
import NavbarComponent from './components/navbar';

export interface VerticalLayoutProps {
  navbar?: JSX.Element;
  footer?: JSX.Element;
  menu?: JSX.Element;
}

// ** Vars
const footerClasses: Record<string, string> = {
  static: 'footer-static',
  sticky: 'footer-fixed',
  hidden: 'footer-hidden',
};

const navbarWrapperClasses: Record<string, string> = {
  floating: 'navbar-floating',
  sticky: 'navbar-sticky',
  static: 'navbar-static',
  hidden: 'navbar-hidden',
};

const navbarClasses: Record<string, string> = {
  floating: 'floating-nav',
  sticky: 'fixed-top',
  static: 'navbar-static-top',
  hidden: 'd-none',
};

const VerticalLayout: React.FC<VerticalLayoutProps> = ({
  children,
  navbar,
  footer,
  menu,
}) => {
  // ** Hooks
  const [skin] = useSkin();
  const [navbarType] = useNavbarType();
  const [footerType] = useFooterType();
  const [navbarColor] = useNavbarColor();

  // ** States
  const [isMounted, setIsMounted] = useState(false);
  const [menuVisibility, setMenuVisibility] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // ** Update Window Width
  const handleWindowWidth = () => {
    setWindowWidth(window.innerWidth);
  };

  // ** Vars
  const location = useLocation();
  const { menuCollapsed, menuHidden } = useLayoutContext();

  //** This function will detect the Route Change and will hide the menu on menu item click
  useEffect(() => {
    if (menuVisibility && windowWidth < 1200) {
      setMenuVisibility(false);
    }
    // do not update on menuVisibility otherwise it toggles it back to false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, windowWidth]);

  //** Sets Window Size & Layout Props
  useEffect(() => {
    if (window !== undefined) {
      window.addEventListener('resize', handleWindowWidth);
    }
  }, [windowWidth]);

  //** ComponentDidMount
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  const bgColorCondition =
    navbarColor !== '' && navbarColor !== 'light' && navbarColor !== 'white';

  if (!isMounted) {
    return null;
  }

  return (
    <div
      className={classnames(
        `wrapper vertical-layout ${
          navbarWrapperClasses[navbarType] || 'navbar-floating'
        } ${footerClasses[footerType] || 'footer-static'}`,
        {
          // Modern Menu
          'vertical-menu-modern': windowWidth >= 1200,
          'menu-collapsed': menuCollapsed && windowWidth >= 1200,
          'menu-expanded': !menuCollapsed && windowWidth > 1200,

          // Overlay Menu
          'vertical-overlay-menu': windowWidth < 1200,
          'menu-hide': !menuVisibility && windowWidth < 1200,
          'menu-open': menuVisibility && windowWidth < 1200,
        }
      )}
      {...(menuHidden ? { 'data-col': '1-column' } : {})}
    >
      {!menuHidden ? (
        <SidebarComponent
          skin={skin}
          menu={menu}
          menuCollapsed={menuCollapsed}
        />
      ) : null}

      <Navbar
        expand="lg"
        light={skin !== 'dark'}
        dark={skin === 'dark' || bgColorCondition}
        color={bgColorCondition ? navbarColor : undefined}
        className={classnames(
          `header-navbar navbar align-items-center ${
            navbarClasses[navbarType] || 'floating-nav'
          } navbar-shadow`
        )}
      >
        <div className="navbar-container d-flex content">
          {navbar || <NavbarComponent setMenuVisibility={setMenuVisibility} />}
        </div>
      </Navbar>

      {children}

      <div
        className={classnames('sidenav-overlay', {
          show: menuVisibility,
        })}
        onClick={() => setMenuVisibility(false)}
      ></div>

      <footer
        className={classnames(
          `footer footer-light ${footerClasses[footerType] || 'footer-static'}`,
          {
            'd-none': footerType === 'hidden',
          }
        )}
      >
        {footer ? footer : <FooterComponent />}
      </footer>

      <CookieBanner />

      <div className="scroll-to-top">
        <ScrollToTop showUnder={300} style={{ bottom: '35px' }}>
          <Button className="btn-icon" color="primary">
            <ArrowUp size={14} />
          </Button>
        </ScrollToTop>
      </div>
    </div>
  );
};

export default VerticalLayout;
